import { get, writable } from 'svelte/store';
import {
  createInstance,
  type Client as OptimizelyClient,
  type OptimizelyUserContext,
} from '@optimizely/optimizely-sdk';
import { PUBLIC_OPTIMIZELY_KEY } from '$env/static/public';
import { userStore } from './userStore';
import { analytics } from '$lib/gateway/segment';
import { type VariationItem, type VariationContainerProps } from '$lib/types/contentTypes';

export const optimizelyStore = writable<{
  client: OptimizelyClient | null;
  userContext: OptimizelyUserContext | null;
  isReady: boolean;
  isInitializing: boolean;
}>({ client: null, userContext: null, isReady: false, isInitializing: false });

let optimizelyPromise: Promise<void> | null = null;

export function ensureOptimizely() {
  if (get(optimizelyStore).isReady || optimizelyPromise) {
    return optimizelyPromise;
  }

  optimizelyPromise = initOptimizely();

  return optimizelyPromise;
}

export async function initOptimizely() {
  const initialValue = get(optimizelyStore);

  if (initialValue.isInitializing || initialValue.isReady) {
    return;
  }

  optimizelyStore.update((state) => ({ ...state, isInitializing: true }));

  try {
    const optimizelyClient = createInstance({
      sdkKey: PUBLIC_OPTIMIZELY_KEY,
    });

    const readyState = await optimizelyClient?.onReady();

    if (!readyState?.success) {
      throw new Error('Optimizely failed to initialize');
    }

    const user = get(userStore).user;

    const userId =
      user?.user_metadata?.user_canonical_id ||
      (analytics.instance?.user().anonymousId() as string);

    const userContext = optimizelyClient?.createUserContext(userId);

    if (!userContext) {
      throw new Error('Optimizely failed to get user context');
    }

    optimizelyStore.set({
      client: optimizelyClient,
      userContext: userContext,
      isReady: readyState.success,
      isInitializing: false,
    });
  } catch (error) {
    optimizelyStore.set({
      client: null,
      userContext: null,
      isReady: false,
      isInitializing: false,
    });
  } finally {
    optimizelyPromise = null;
  }
}

export function getVariationItem<T extends object = object>(
  variationContainer: VariationContainerProps<T>,
) {
  let variationItem: (VariationItem & T) | undefined;

  if (variationContainer.variationsCollection?.items?.length) {
    variationItem =
      variationContainer.variationsCollection.items.find(
        (item) => item.sys.id === variationContainer.meta['off'],
      ) || variationContainer.variationsCollection.items[0];
  }

  const optimizely = get(optimizelyStore);

  if (optimizely.userContext) {
    const decision = optimizely.userContext.decide(variationContainer.flagKey);

    const variationKey = decision?.variationKey;

    const item = variationKey
      ? variationContainer.variationsCollection.items.find(
          (item) => item.sys.id === variationContainer.meta[variationKey],
        )
      : null;

    if (item) {
      variationItem = item;
    }
  }

  return variationItem;
}
